import dayjs from 'dayjs';
import { TriangleAlert } from 'lucide-react';
import { Tag } from '../../ui';
import { inr2 } from '../../lib/format';
import { formatIstDate } from '../../lib/time';
import { RoleBadge } from '../../components/RoleBadge';
import { AttachmentCell } from '../../components/AttachmentPreview';
import { isTravelEcho, VERIFY_COLOR } from './ExpenseReportDetailPage';
import { ClaimMoneyPicture } from './ClaimMoneyPicture';

/** Expenses that must carry proof and do not — the server's own `proofRequired` flag per expense. */
export function proofGapsOf(report: any): any[] {
  return (report?.items ?? []).filter((i: any) => i.proofRequired && !i.billUrl);
}

/**
 * THE WHOLE CLAIM, AS AN APPROVER NEEDS TO SEE IT — one component for every place a claim is decided.
 *
 * The register's "Review & approve" and the approvals hub's Approve / Reject / Send back / Mark paid
 * dialogs were two different pictures of the same claim: the register showed every expense with its
 * proof and the money behind it, the hub showed "What is claimed · TRAVEL ₹192" and "Bills attached 0 of
 * 1". The approver deciding in the hub was deciding on less. Both now draw this.
 *
 * `report` is `GET /expense-reports/:id` (items carry `proofRequired`/`proofLabel`; `moneyPicture`).
 * `amount`, `fundingSource`, `cashPart` are the decision being typed right now, so the money picture
 * answers the form as it changes.
 */
export function ExpenseClaimReview({ report, mode = 'approve', amount, fundingSource, cashPart, hideMoney = false, canOverrideProof = false }: {
  report: any;
  mode?: 'approve' | 'pay';
  amount?: number | null;
  fundingSource?: string | null;
  cashPart?: number | null;
  /** The settlement dialog draws the money itself from the server preview — skip the legacy picture. */
  hideMoney?: boolean;
  /** A Super Admin may approve without required proof (with a reason) — say so instead of "can't be approved". */
  canOverrideProof?: boolean;
}) {
  const items: any[] = report?.items ?? [];
  const claimed = Number(report?.subtotalAmount ?? 0);
  const required = items.filter((i) => i.proofRequired);
  const missing = proofGapsOf(report);
  const unverified = items.filter((i) => (i.verificationStatus ?? 'PENDING') === 'PENDING');

  return (
    <div className="space-y-3">
      {/* Whose claim, which day, and what the day adds up to. */}
      <div className="rounded-card border border-line bg-surface-2 px-3 py-2.5 text-sm">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-semibold text-content">{report.repName ?? `#${report.repId}`}</span>
          <RoleBadge role={report.repRole} />
          {report.expenseDate && (
            <span className="text-muted">
              · field day {formatIstDate(report.expenseDate)} ({dayjs(report.expenseDate).format('dddd')})
            </span>
          )}
        </div>
        <div className="mt-1 text-muted">
          {items.length} {items.length === 1 ? 'expense' : 'expenses'}
          {Number(report.totalDistanceKm) > 0 && <> · {Number(report.totalDistanceKm)} km</>}
          {Number(report.daAmount) > 0 && <> · daily allowance {inr2(report.daAmount)}</>}
          {' · claimed '}<span className="font-semibold text-content">{inr2(claimed)}</span>
        </div>
        {report.submittedAt && (
          <div className="mt-0.5 type-caption text-subtle">
            submitted by {report.submittedByName ?? report.repName} · {formatIstDate(report.submittedAt)}
          </div>
        )}
      </div>

      {/* What an approver has to notice before saying yes — said once, above the evidence. */}
      {mode === 'approve' && missing.length > 0 && (
        <div className="flex items-start gap-2 rounded-card border border-danger-500/40 bg-danger-500/10 px-3 py-2.5 text-sm text-danger-600 dark:text-danger-500">
          <TriangleAlert size={16} className="mt-0.5 shrink-0" aria-hidden />
          <div>
            <div className="font-medium">
              {missing.length === 1 ? '1 expense is missing its proof' : `${missing.length} of ${required.length} expenses are missing their proof`} — {canOverrideProof ? 'only a Super Admin can approve it without the proof.' : 'this claim can’t be approved.'}
            </div>
            <div>
              {canOverrideProof
                ? `Send it back so ${report.repName ?? 'the rep'} can attach it, or approve without it and record why.`
                : `Send it back so ${report.repName ?? 'the rep'} can attach it and submit again.`}
            </div>
          </div>
        </div>
      )}
      {mode === 'approve' && unverified.length > 0 && (
        <div className="flex items-start gap-2 rounded-card border border-warning-500/40 bg-warning-500/10 px-3 py-2.5 text-sm text-warning-600 dark:text-harvest-300">
          <TriangleAlert size={16} className="mt-0.5 shrink-0" aria-hidden />
          <div>{unverified.length} of {items.length} {items.length === 1 ? 'expense is' : 'expenses are'} not verified yet — approving marks them verified at the claimed amount.</div>
        </div>
      )}

      {/* Every expense as a numbered card: what it was, how it was priced, its proof, verification. */}
      <ol className="space-y-2">
        {items.map((i, n) => {
          const text = String(i.description ?? '').trim();
          const showText = text && !isTravelEcho(text, i);
          const status = i.verificationStatus ?? 'PENDING';
          return (
            <li key={i.id} className="flex flex-wrap items-start gap-3 rounded-card border border-line bg-surface px-3 py-2.5">
              <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-surface-3 type-caption font-semibold text-muted">{n + 1}</span>
              {/* basis-48: on a phone the proof and the amount wrap under the text instead of squeezing it to a letter a line. */}
              <div className="min-w-0 flex-1 basis-48">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Tag>{String(i.category ?? '').replace(/_/g, ' ')}</Tag>
                  {!!i.isSystem && <Tag color="blue">auto</Tag>}
                  {i.travelMode && (
                    <span className="text-sm text-content">{i.travelMode} · {Number(i.distanceKm)} km @ {inr2(i.ratePerKm)}/km</span>
                  )}
                </div>
                {showText && <div className="mt-1 whitespace-pre-wrap wrap-break-word text-sm text-content">{text}</div>}
                {(i.vendorName || i.billNumber) && (
                  <div className="mt-0.5 type-caption text-muted">
                    {[i.vendorName, i.billNumber && `bill ${i.billNumber}`].filter(Boolean).join(' · ')}
                  </div>
                )}
                {i.remarks && <div className="mt-0.5 type-caption text-muted">{i.remarks}</div>}
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <Tag color={status === 'VERIFIED' && i.proofRequired && !i.billUrl ? 'orange' : (VERIFY_COLOR[status] ?? 'default')}>
                    {status.replace(/_/g, ' ')}
                  </Tag>
                  {status === 'VERIFIED' && i.proofRequired && !i.billUrl && (
                    <span className="type-caption text-warning-600 dark:text-harvest-300">but no proof is attached</span>
                  )}
                  {status === 'AMOUNT_MISMATCH' && i.verifiedAmount != null && (
                    <span className="type-caption text-muted">verified {inr2(i.verifiedAmount)}</span>
                  )}
                  {i.rejectionReason && <span className="type-caption text-danger-600 dark:text-danger-500">{i.rejectionReason}</span>}
                </div>
              </div>
              {/* The proof beside what it proves: a small preview that opens in page, or a plain
                  statement that it is missing and what should be there. */}
              <div className="flex shrink-0 items-center">
                {i.billUrl ? (
                  <AttachmentCell files={[i.billUrl]} label={`${i.proofLabel ?? 'Proof'} · expense ${n + 1}`} size={40} />
                ) : i.proofRequired ? (
                  <span className="rounded-full border border-danger-500/40 bg-danger-500/10 px-2 py-0.5 type-caption font-medium text-danger-600 dark:text-danger-500">
                    {i.proofLabel ?? 'Proof'} missing
                  </span>
                ) : (
                  <span className="type-caption text-subtle">no proof needed</span>
                )}
              </div>
              <div className="shrink-0 text-right">
                <span className="type-body-strong tabular-nums text-content">{inr2(i.amount)}</span>
              </div>
            </li>
          );
        })}
      </ol>

      {/* Where the money comes from — answering the decision being typed. */}
      {!hideMoney && report.moneyPicture && (
        <ClaimMoneyPicture
          picture={report.moneyPicture} mode={mode} amount={amount}
          fundingSource={fundingSource} cashPart={cashPart}
          positionOnly={mode === 'approve'}
        />
      )}
    </div>
  );
}
